import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, MapPin, Truck, Scissors, Languages } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { Greeting } from "./Greeting";
import { waLink, WA, IMAGES, SITE } from "../config/site";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCardA = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yCardB = useTransform(scrollYProgress, [0, 1], [0, -130]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden">
      <div className="absolute inset-0 gold-glow" aria-hidden="true" />
      <div
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold/[0.07] blur-3xl"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-28 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex flex-wrap items-center gap-2.5 border border-gold/30 bg-gold/[0.08] rounded-full px-4 py-1.5 mb-7"
              data-testid="hero-welcome-pill"
            >
              <Greeting className="text-gold text-sm" />
              <span className="w-1 h-1 rounded-full bg-gold/60" />
              <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-cream/80">
                Udayapura · Kanakapura Main Road
              </span>
            </motion.div>

            <h1 className="font-display font-bold tracking-tight text-cream text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.06] text-balance">
              <MaskedLine delay={0.15}>Everything You Need.</MaskedLine>
              <MaskedLine delay={0.3}>
                All Under{" "}
                <span className="italic font-medium text-gold">One Roof.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="mt-6 text-base sm:text-lg text-cream/65 leading-relaxed max-w-xl"
              data-testid="hero-subtitle"
            >
              From everyday fashion and footwear to alterations, innerwear,
              accessories and home-comfort essentials — discover convenience
              for the whole family at {SITE.name}.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href={waLink(WA.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="group inline-flex items-center gap-2.5 bg-gold text-charcoal font-semibold rounded-full px-7 py-3.5 hover:bg-gold-hover transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_40px_-10px_rgba(212,175,55,0.5)]"
              >
                <MessageCircle size={18} />
                Enquire on WhatsApp
              </a>
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-directions-cta"
                className="inline-flex items-center gap-2.5 border border-cream/25 text-cream rounded-full px-7 py-3.5 hover:border-gold hover:text-gold transition-all duration-300 hover:-translate-y-0.5"
              >
                <MapPin size={17} />
                Get Directions
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
              data-testid="hero-trust-points"
            >
              {[
                { icon: Scissors, label: "In-house alterations" },
                { icon: Truck, label: "Free local delivery" },
                { icon: MapPin, label: `Open daily ${SITE.hoursShort}` },
                { icon: Languages, label: "Hindi · Kannada · English · Rajasthani · Haryanvi" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 text-sm text-cream/55"
                >
                  <Icon size={15} className="text-gold" /> {label}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            {/* Mobile: shop image always visible on first open */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
              className="md:hidden relative rounded-3xl overflow-hidden img-frame border border-gold/25"
              data-testid="hero-shop-image-mobile"
            >
              <img
                src={IMAGES.heroMain}
                alt="Inside Om Sri Sri Collection store at Udayapura — footwear, garments, bags and essentials"
                className="w-full h-60 sm:h-72 object-cover"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                Our store · Udayapura
              </p>
            </motion.div>

            {/* Desktop: shop interior as the main frame, products as floating cards */}
            <div className="hidden md:block relative h-[440px] sm:h-[520px]">
              <motion.div
                style={{ y: yMain }}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                className="absolute inset-x-0 top-0 h-[72%] rounded-[2rem] overflow-hidden img-frame border border-white/10"
                data-testid="hero-shop-image"
              >
                <img
                  src={IMAGES.heroMain}
                  alt="Inside Om Sri Sri Collection store at Udayapura — footwear, garments, bags and essentials"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchpriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/55 via-transparent to-transparent" />
                <p className="absolute top-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold bg-charcoal/50 backdrop-blur-md rounded-full px-3.5 py-1.5 border border-white/10">
                  Our store · Udayapura, Kanakapura Main Road
                </p>
              </motion.div>
              <motion.div
                style={{ y: yCardA }}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.65, ease: EASE }}
                className="absolute left-4 bottom-0 w-[34%] rounded-3xl overflow-hidden img-frame border border-gold/30 animate-float-slow"
              >
                <img
                  src={IMAGES.heroKurtis}
                  alt="Colourful kurtis and ladies' wear on display at Om Sri Sri Collection"
                  className="w-full h-44 sm:h-52 object-cover object-top"
                  loading="lazy"
                />
              </motion.div>
              <motion.div
                style={{ y: yCardB }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.85, ease: EASE }}
                className="absolute right-4 bottom-6 w-[40%] rounded-3xl overflow-hidden img-frame border border-white/15"
              >
                <img
                  src={IMAGES.heroBags}
                  alt="Printed handbags and sling bags on the rack at Om Sri Sri Collection"
                  className="w-full h-36 sm:h-40 object-cover"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid="delivery-strip-banner"
        className="relative border-y border-gold/25 bg-gradient-to-r from-gold/[0.12] via-gold/[0.18] to-gold/[0.12]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5 text-center">
          <span className="inline-flex items-center gap-2.5 font-display text-xl sm:text-2xl font-semibold text-gold tracking-wide">
            <Truck size={22} strokeWidth={2.2} />
            FREE DELIVERY WITHIN 5 KM
          </span>
          <span className="hidden sm:block w-px h-6 bg-gold/30" />
          <span className="font-mono text-xs sm:text-sm uppercase tracking-[0.2em] text-cream/75">
            On orders above ₹1,500
          </span>
        </div>
      </div>
    </section>
  );
};
